import React, { FC, useState } from "react";
import { Table, Button, Popconfirm, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/lib/table";
import { TeamMembershipIds, Phase, TeamMembership } from "../../data-types";
import { TeamMembershipPartsFragment } from "~/gql/graphql";

import styles from "./team-builder.module.css";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["container"]: containerClassName,
  ["header"]: headerClassName,
  ["form-container"]: formContainerClassName,
  ["form"]: formClassName,
  ["label"]: labelClassName,
  ["table-container"]: tableContainerClassName,
  ["table"]: tableClassName,
  ["push-to-phases-button"]: pushToPhasesButtonClassName,
} = styles as any;

type UpdateMatterTeamInput = {
  teamMembersToAdd: TeamMembershipIds[];
  teamMembersToRemove: TeamMembershipIds[];
};

type AssignToPhaseDropdownProps = {
  teamMember: TeamMembershipPartsFragment;
  phases: Phase[];
  onAssignTeamToPhases: (
    teamMembers: TeamMembershipPartsFragment[],
    phases: Phase[],
    message?: string
  ) => void;
};

export const AssignToPhaseDropdown: FC<AssignToPhaseDropdownProps> = ({
  teamMember,
  phases,
  onAssignTeamToPhases,
}) => {
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);

  const handleChange = (value: number) => {
    if (value === -1) {
      onAssignTeamToPhases([teamMember], phases, "Assigned to all phases");
    }
    const phase = phases.find((p) => p.id === value);
    if (phase) {
      onAssignTeamToPhases(
        [teamMember],
        [phase],
        `Assigned to ${phase.title ? phase.title : `Phase ${phase.order + 1}`}`
      );
    }
    setSelectedPhase(null);
  };

  const options = [
    ...phases.map((phase) => ({
      label: phase.title ? phase.title : `Phase ${phase.order + 1}`,
      value: phase.id,
    })),
    {
      label: (
        <span>
          <b>Assign to all phases</b>
        </span>
      ),
      value: -1,
    },
  ];

  return (
    <Select
      value={selectedPhase}
      placeholder="Select a phase"
      style={{ width: "100%" }}
      onChange={handleChange}
      options={options}
    />
  );
};

type TeamBuilderProps = {
  teamRates: TeamMembershipPartsFragment[];
  phases: Phase[];
  assignedRateIds: number[];
  feeEarnerCascadeRender: (values: {
    selectedFeeEarnerId?: number | null;
    selectedRateId?: number | null;
    selectedFeeEarnerLevelId?: number | null;
    onChange: (values: {
      feeEarnerId: number | null;
      selectedRateId: number;
      feeEarnerLevelId: number | null;
    }) => void;
    onClear: () => void;
  }) => React.ReactNode;
  currencySymbol: string;
  handleUpdateMatterTeam: (data: UpdateMatterTeamInput) => void;
  allowAssignToPhases?: boolean;
  onAssignTeamToPhases: (teamMembers: TeamMembership[], phases: Phase[], message?: string) => void;
  assignWorksLoading: boolean;
  findMatterLoading: boolean;
};

export const TeamBuilder: FC<TeamBuilderProps> = ({
  teamRates,
  phases,
  assignedRateIds,
  feeEarnerCascadeRender,
  currencySymbol,
  handleUpdateMatterTeam,
  allowAssignToPhases,
  onAssignTeamToPhases,
  assignWorksLoading,
  findMatterLoading,
}) => {
  const [selectedFeeEarnerId, setSelectedFeeEarnerId] = useState<number | null>(null);
  const [selectedFeeEarnerLevelId, setSelectedFeeEarnerLevelId] = useState<number | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<number | null>(null);

  const taxonomy = useTaxonomyLabel();

  const handleDelete = (rateId: number, feeEarnerId: number | null) => {
    handleUpdateMatterTeam({
      teamMembersToAdd: [],
      teamMembersToRemove: [
        {
          rateId,
          feeEarnerId,
        },
      ],
    });
  };

  const handlePushToPhases = () => onAssignTeamToPhases(teamRates as TeamMembership[], phases);

  const tableColumns: ColumnType<TeamMembershipPartsFragment>[] = [
    {
      title: taxonomy.FeeEarner,
      dataIndex: ["feeEarner", "name"],
      key: "feeEarner.name",
      render: (name: string) => name || "N/A",
    },
    {
      title: taxonomy.FeeEarnerLevel,
      dataIndex: ["feeEarner", "feeEarnerLevel", "description"],
      key: "feeEarner.feeEarnerLevel.description",
      render: (description: string, record: TeamMembershipPartsFragment) =>
        description || record.rate.feeEarnerLevel?.description || "N/A",
    },
    {
      title: "Amount",
      dataIndex: ["rate", "amount"],
      key: "amount",
      render: (amount: number) => `${currencySymbol}${amount}`,
    },
    {
      title: "Rate description",
      dataIndex: ["rate", "description"],
      key: "description",
    },
  ];
  tableColumns.push({
    title: "Remove",
    key: "remove",
    render: (text: any, record: TeamMembershipPartsFragment) => (
      <div>
        {assignedRateIds.includes(record.rateId) ? (
          <Popconfirm
            placement="right"
            title={
              <div style={{ maxWidth: "400px" }}>
                <b>Warning:</b> This team member has been assigned to a phase. Deleting them will
                remove them from all phases.
              </div>
            }
            onConfirm={(e) => {
              if (e) {
                e.stopPropagation();
                handleDelete(record.rateId, record.feeEarnerId);
              }
            }}
            onCancel={(e) => {
              if (e) {
                e.stopPropagation();
              }
            }}
            okText="OK"
            cancelText="Cancel"
          >
            <span style={{ color: "red" }}>
              <DeleteOutlined onClick={() => {}} />
            </span>
          </Popconfirm>
        ) : (
          <span style={{ color: "red" }}>
            <DeleteOutlined onClick={() => handleDelete(record.rateId, record.feeEarnerId)} />
          </span>
        )}
      </div>
    ),
    align: "center",
  });

  return (
    <div className={containerClassName}>
      <div className={headerClassName}>
        <h4>
          Use this screen to add Team members to your proposal. Any Teams members added here will be
          displayed on the proposal and can be added to the phases if building a{" "}
          {taxonomy.FeeEarner} based pricing proposal.
        </h4>
      </div>
      <div className={formContainerClassName}>
        <span className={labelClassName}>
          Select a {taxonomy.FeeEarner} and Rate to add to team:
        </span>
        <div className={formClassName}>
          {feeEarnerCascadeRender({
            selectedFeeEarnerId,
            selectedFeeEarnerLevelId,
            selectedRateId,
            onChange: (values) => {
              setSelectedFeeEarnerId(values.feeEarnerId);
              setSelectedRateId(values.selectedRateId);
              setSelectedFeeEarnerLevelId(values.feeEarnerLevelId);
              if (values.selectedRateId) {
                handleUpdateMatterTeam({
                  teamMembersToAdd: [
                    {
                      rateId: values.selectedRateId,
                      feeEarnerId: values.feeEarnerId,
                    },
                  ],
                  teamMembersToRemove: [],
                });
              }
            },
            onClear: () => {
              setSelectedFeeEarnerId(null);
              setSelectedRateId(null);
              setSelectedFeeEarnerLevelId(null);
            },
          })}
        </div>
      </div>
      <div>
        <h2>Team</h2>
        <div className={tableContainerClassName}>
          <Table
            className={tableClassName}
            dataSource={teamRates}
            columns={tableColumns}
            rowKey={(record) => record.id}
            pagination={false}
            loading={findMatterLoading || assignWorksLoading}
          />
        </div>
      </div>
      {allowAssignToPhases && (
        <div className={pushToPhasesButtonClassName}>
          {assignedRateIds.length > 0 ? (
            <Popconfirm
              placement="right"
              title="Are you sure? This will overwrite any existing phase assignments."
              disabled={phases.length === 0 || teamRates.length === 0 || assignWorksLoading}
              onConfirm={(e) => {
                if (e) {
                  e.stopPropagation();
                  handlePushToPhases();
                }
              }}
              onCancel={(e) => {
                if (e) {
                  e.stopPropagation();
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                disabled={phases.length === 0 || teamRates.length === 0 || assignWorksLoading}
              >
                Assign all to all Phases
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="primary"
              disabled={phases.length === 0 || teamRates.length === 0 || assignWorksLoading}
              onClick={handlePushToPhases}
            >
              Assign all to all Phases
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
